import { useQuery as useApolloQuery } from '@apollo/client';
import type { ApprovalLevelRow } from '@risksmart-app/drizzle/src/dto';
import { GetApprovalLevelsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useIsFeatureFlagEnabled } from 'src/hooks/useIsFeatureFlagEnabled';
import { useTRPC } from 'src/utils/trpc';

/**
 * Configured approval levels for a (workflow, parentId) pair (RSP-6087) —
 * 1-to-1 with the legacy Hasura `getApprovalLevels` query. The data layer
 * resolves BOTH the per-entity approval (ParentId = the entity) AND the
 * org-wide GLOBAL approval (ParentId IS NULL). Flag-gated: `trpc` on → tRPC
 * `frontend.approval.approvalLevels`; off → the legacy Apollo query.
 *
 * Returns the FULL configured level set; the consumer filters it down to the
 * levels present on the CR's responses (exactly as the legacy panel did).
 */
export const useGetApprovalLevels = (
  workflow: string | undefined,
  parentId: string
): { levels: ApprovalLevelRow[] | undefined; loading: boolean } => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();

  const trpcQuery = useQuery({
    ...trpc.frontend.approval.approvalLevels.queryOptions({
      workflow: workflow ?? '',
      parentId,
    }),
    enabled: trpcEnabled && !!workflow,
  });

  const apolloQuery = useApolloQuery(GetApprovalLevelsDocument, {
    variables: {
      Workflow: workflow ?? '',
      ParentId: parentId,
    },
    skip: trpcEnabled || !workflow,
  });

  if (trpcEnabled) {
    return { levels: trpcQuery.data, loading: trpcQuery.isLoading };
  }

  return {
    levels: apolloQuery.data?.levels.map((level) => ({
      Id: level.Id,
      ApprovalRuleType: level.ApprovalRuleType,
    })),
    loading: apolloQuery.loading,
  };
};
